'use client';

import { useMemo } from 'react';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';
import { useTranslations } from 'next-intl';
import { Doughnut } from 'react-chartjs-2';

import { cn } from '@/lib/utils/cn';
import { formatDecimal } from '@/lib/utils/formatDecimal';
import { T_Pool } from '@/ts/types/T_Pool';

import { calculateTokensRemaining } from './tokenDataUtils';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

interface I_TokenDistributionChartProps {
  token: T_Pool;
  className?: string;
}

export function TokenDistributionChart({
  token,
  className = ''
}: I_TokenDistributionChartProps) {
  const t = useTranslations('pools');

  // Memoize expensive calculations
  const chartData = useMemo(() => {
    const color = token.color;
    const tokensSoldStr = token?.blockchainData?.totalSoldTokensDecimal || '0';
    const tokensRemainingStr = calculateTokensRemaining(token);

    const tokensSold = parseFloat(tokensSoldStr) || 0;
    const tokensAvailable = parseFloat(tokensRemainingStr) || 0;

    // Validate values to prevent NaN or Infinity
    return {
      color,
      tokensSold: isFinite(tokensSold) && !isNaN(tokensSold) ? tokensSold : 0,
      tokensAvailable:
        isFinite(tokensAvailable) && !isNaN(tokensAvailable)
          ? tokensAvailable
          : 0
    };
  }, [token]);

  // Memoize chart data
  const data = useMemo(
    () => ({
      labels: [t('sold'), t('remaining')],
      datasets: [
        {
          data: [chartData.tokensSold, chartData.tokensAvailable],
          backgroundColor: [chartData.color, 'hsla(240, 5%, 26%, 1)'],
          borderColor: [chartData.color, 'hsla(240, 5%, 26%, 1)'],
          borderWidth: 0,
          cutout: '80%'
        }
      ]
    }),
    [chartData.tokensSold, chartData.tokensAvailable, chartData.color, t]
  );

  // Memoize chart options
  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false // Hide default legend
        },
        tooltip: {
          enabled: false // Disable tooltips for cleaner look
        }
      },
      elements: {
        arc: {
          borderWidth: 0
        }
      }
    }),
    []
  );

  // Memoize percentage calculation
  const soldPercentage = useMemo(() => {
    const total = chartData.tokensSold + chartData.tokensAvailable;
    if (total === 0 || !isFinite(total) || isNaN(total)) return '0';

    const percentage = (chartData.tokensSold / total) * 100;
    // Validate percentage to prevent NaN or Infinity
    if (!isFinite(percentage) || isNaN(percentage)) return '0';

    return formatDecimal(percentage, 1);
  }, [chartData.tokensSold, chartData.tokensAvailable]);

  return (
    <div className={cn('relative', className)}>
      <div className="relative w-full h-64 flex items-center justify-center">
        <Doughnut data={data} options={options} />

        {/* Center text - matching the screenshot design */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-center">
            <div className="text-3xl md:text-4xl font-bold text-foreground mb-1">
              {soldPercentage}%
            </div>
            <div className="text-sm text-muted-foreground font-medium">
              {t('presaleFilled')}
            </div>
          </div>
        </div>
      </div>

      {/* Custom Legend - matching screenshot layout */}
      <div className="flex justify-center space-x-8 mt-6">
        <div className="flex items-center space-x-2">
          <div
            className="w-4 h-4 rounded-xs"
            style={{ backgroundColor: chartData.color }}
          />
          <span className="text-sm text-foreground font-medium">
            {t('sold')}
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <div
            className="w-4 h-4 rounded-xs"
            style={{ backgroundColor: 'hsla(240, 5%, 26%, 1)' }}
          />
          <span className="text-sm text-foreground font-medium">
            {t('remaining')}
          </span>
        </div>
      </div>
    </div>
  );
}
